import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import UploadZone from '../../components/shared/UploadZone';
import { Bell, CheckCircle2, FileText, Clock } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'https://api.orbit-technology.com/api';

// No Application category here on purpose - the client portal never needs to
// collect a signed application from the merchant directly. It arrives
// through the DocuSign envelope Orbit already generates and tracks, so
// asking for it again here was a redundant, confusing upload slot.
const DOC_CATEGORIES = [
  { id: 'bank_statements',  label: 'Bank Statements',      icon: '🏦' },
  { id: 'drivers_license',  label: "Driver's License / ID", icon: '🪪' },
  { id: 'voided_check',     label: 'Voided Check',         icon: '✅' },
  { id: 'other',            label: 'Other',                icon: '📂' },
];

export default function UploadCenter() {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const clientId = user?.client_id || user?.clientId;

  const [docs, setDocs]               = useState([]);
  const [requests, setRequests]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [refreshKey, setRefreshKey]   = useState(0);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!clientId) { setLoading(false); return; }
    Promise.all([
      fetch(`${API}/documents/client/${clientId}`, { headers }).then(r => r.ok ? r.json() : []),
      fetch(`${API}/clients-api/requests/all`, { headers }).then(r => r.ok ? r.json() : []),
    ]).then(([d, r]) => {
      setDocs(d);
      setRequests(r.filter(req => req.clientId === clientId && req.status !== 'fulfilled'));
    }).catch(() => {}).finally(() => setLoading(false));
  }, [clientId, refreshKey]);

  const handleUploadComplete = () => setTimeout(() => setRefreshKey(k => k + 1), 1500);

  const uploadedCategories = new Set(docs.map(d => d.category));

  if (!clientId) return (
    <div className="px-1 py-5 md:p-6 text-gray-400 text-sm">Profile not linked to a client record. Contact your representative.</div>
  );

  if (loading) return <div className="px-1 py-5 md:p-6 text-gray-400 text-sm">Loading...</div>;

  return (
    <div className="px-1 py-5 md:p-6 space-y-6 max-w-2xl mx-auto">
      <div>
        <h1 className="text-xl font-bold text-gray-900">{t('Upload Center')}</h1>
        <p className="text-gray-400 text-sm mt-0.5">Upload your documents securely below.</p>
      </div>

      {/* Pending requests */}
      {requests.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Bell size={14} className="text-amber-500" />
            <h2 className="text-amber-600 font-semibold text-sm">Requested ({requests.length})</h2>
          </div>
          {requests.map(req => {
            const cat = DOC_CATEGORIES.find(c => c.id === req.category) || { icon: '📄', label: req.category };
            return (
              <div key={req.id} className="bg-white border border-amber-200 rounded-2xl p-5 shadow-sm">
                <div className="flex items-start justify-between max-md:gap-3 mb-3">
                  <div>
                    <p className="text-gray-900 font-semibold text-sm">{cat.icon} {req.catLabel || cat.label}</p>
                    {req.instructions && <p className="text-gray-400 text-xs mt-1">{req.instructions}</p>}
                  </div>
                  <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2 py-1 rounded-lg max-md:shrink-0">
                    <Clock size={10} /> Pending
                  </span>
                </div>
                <UploadZone
                  category={req.category}
                  categoryLabel={`Upload ${req.catLabel || cat.label}`}
                  clientId={clientId}
                  uploadedBy={user?.id}
                  token={token}
                  onUpload={handleUploadComplete}
                  compact
                />
              </div>
            );
          })}
        </div>
      )}

      {/* All categories */}
      <div>
        <h2 className="text-gray-700 font-semibold text-sm mb-3">Required Documents</h2>
        <div className="grid grid-cols-1 gap-4">
          {DOC_CATEGORIES.filter(c => c.id !== 'other').map(cat => {
            const catDocs = docs.filter(d => d.category === cat.id);
            const uploaded = catDocs.length > 0;
            return (
              <div key={cat.id} className={`bg-white border rounded-2xl p-5 shadow-sm ${uploaded ? 'border-green-200' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-gray-800 font-semibold text-sm">{cat.icon} {cat.label}</p>
                  {uploaded && (
                    <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                      <CheckCircle2 size={12} /> {catDocs.length} uploaded
                    </span>
                  )}
                </div>
                {/* Show existing files */}
                {catDocs.length > 0 && (
                  <div className="mb-3 space-y-1.5">
                    {catDocs.map(d => (
                      <div key={d.id} className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
                        <FileText size={11} className="text-gray-400 shrink-0" />
                        <span className="truncate">{d.fileName}</span>
                        <span className="ml-auto text-gray-300 shrink-0">{d.fileSize}</span>
                      </div>
                    ))}
                  </div>
                )}
                <UploadZone
                  category={cat.id}
                  categoryLabel={`Upload ${cat.label}`}
                  clientId={clientId}
                  uploadedBy={user?.id}
                  token={token}
                  onUpload={handleUploadComplete}
                  compact
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
